import React, { useRef, useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import Select, { components } from 'react-select';
import styled from 'styled-components';
import { OptionCheckbox } from './icons/option_checkbox';
import { SelectedOptionCheckbox } from './icons/selected_option_checkbox';

export type OptionType = {
  value: any;
  label: string;
};

type SelectProps = {
  options: OptionType[];
  value?: OptionType;
  onChange?: (option: OptionType[] | OptionType | null) => void;
  placeholder?: string;
  isMulti?: boolean;
  withSearch?: boolean;
};

const StyledSelect = styled(Select)`
  & > div {
    width: 154px;
  }
  & .outlined-select__control {
    border: none;
    border-bottom: 1px solid rgba(0, 0, 0, 0.42);
    border-radius: 0;
    background-color: transparent;
  }

  & .outlined-select__control:hover {
    border: none;
    border-bottom: 1px solid rgba(0, 0, 0, 0.42);
    border-radius: 0;
    background-color: transparent;
  }

  & .outlined-select__value-container {
    padding: 0;
    overflow: hidden;
    flex-wrap: nowrap;
    min-width: 0;
  }

  & .outlined-select__placeholder {
    margin-left: 0;
    color: #757575;
    opacity: 0.7;
  }

  & .outlined-select__control--is-focused {
    border: none;
    border-bottom: 1px solid rgba(0, 0, 0, 0.42);
    border-radius: 0;
    box-shadow: none;
    background-color: transparent;
  }

  & .outlined-select__dropdown-indicator,
  .outlined-select__clear-indicator {
    padding: 6px;
  }

  & .outlined-select__control {
    min-height: unset;
  }

  & .outlined-select__menu {
    overflow-x: hidden;
    z-index: 10;
  }

  & .outlined-select__menu-list {
    padding: 0;
    overflow-x: hidden;
  }

  & .outlined-select__option {
    font-size: 12px;
    font-weight: 500;
    font-size: 12px;
    line-height: 24px;
  }

  & .outlined-select__option--is-focused {
    background-color: #f7f7f7 !important;
  }

  & .outlined-select__option--is-selected {
    font-weight: 500;
    background-color: #f7f7f7 !important;
    color: inherit !important;
  }
`;

const DisplayValue = styled.div`
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
`;

const MultipleOption = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  font-weight: 500;
  font-size: 12px;
  line-height: 24px;
  min-width: 0;
  overflow: hidden;
`;

const MenuOptionText = styled.span`
  display: block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
`;

const TooltipBox = styled.div`
  position: fixed;
  background: rgba(50, 50, 50, 0.92);
  color: #fff;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  line-height: 1.4;
  max-width: 280px;
  z-index: 99999;
  pointer-events: none;
  word-break: break-word;
  white-space: normal;
`;

function useTextTooltip() {
  const ref = useRef<any>(null);
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);

  const onMouseEnter = (e: React.MouseEvent) => {
    if (ref.current && ref.current.scrollWidth > ref.current.clientWidth) {
      setPos({ x: e.clientX + 10, y: e.clientY + 16 });
    }
  };
  const onMouseMove = (e: React.MouseEvent) => {
    if (pos) setPos({ x: e.clientX + 10, y: e.clientY + 16 });
  };
  const onMouseLeave = () => setPos(null);

  return { ref, pos, onMouseEnter, onMouseMove, onMouseLeave };
}

const OptionTextWithTooltip: React.FC<{ text: string }> = ({ text }) => {
  const { ref, pos, onMouseEnter, onMouseMove, onMouseLeave } = useTextTooltip();
  return (
    <>
      <MenuOptionText ref={ref} onMouseEnter={onMouseEnter} onMouseMove={onMouseMove} onMouseLeave={onMouseLeave}>
        {text}
      </MenuOptionText>
      {pos && ReactDOM.createPortal(
        <TooltipBox style={{ left: pos.x, top: pos.y }}>{text}</TooltipBox>,
        document.body
      )}
    </>
  );
};

const DisplayValueWithTooltip: React.FC<{ text: string }> = ({ text }) => {
  const { ref, pos, onMouseEnter, onMouseMove, onMouseLeave } = useTextTooltip();
  return (
    <>
      <DisplayValue ref={ref} onMouseEnter={onMouseEnter} onMouseMove={onMouseMove} onMouseLeave={onMouseLeave}>
        {text}
      </DisplayValue>
      {pos && ReactDOM.createPortal(
        <TooltipBox style={{ left: pos.x, top: pos.y }}>{text}</TooltipBox>,
        document.body
      )}
    </>
  );
};

const Option = ({ children, ...props }: any) => {
  const text = props.data?.label ?? (typeof children === 'string' ? children : '');

  if (!props.isMulti) {
    return (
      <components.Option {...props}>
        <OptionTextWithTooltip text={text} />
      </components.Option>
    );
  }

  return (
    <components.Option {...props}>
      <MultipleOption>
        <div style={{ display: 'flex'}}>{props.isSelected ? <SelectedOptionCheckbox /> : <OptionCheckbox />}</div>
        <OptionTextWithTooltip text={text} />
      </MultipleOption>
    </components.Option>
  );
};

const Placeholder = styled.span`
  font-family: Roboto;
  font-weight: 400;
  font-size: 18px;
  line-height: 100%;
  letter-spacing: 0%;
  color: #757575;
`;

const ValueContainer = (props: any) => {
  const selectedOptions = props.selectProps.value;
  const menuIsOpen = props.selectProps.menuIsOpen;
  const withSearch = props.selectProps.withSearch;
  const isMulti = props.isMulti;
  const hasValue = props.hasValue;

  const displayValue =
    isMulti && Array.isArray(selectedOptions)
      ? selectedOptions?.map((option: OptionType) => option.label)?.join(', ') || ''
      : selectedOptions?.label || '';

  if (withSearch) {
    // react-select always places the Input as the last child
    const children = React.Children.toArray(props.children);
    const input = children[children.length - 1];

    // Hide the display label while the menu is open so the user can type freely
    const showDisplay = isMulti || !menuIsOpen;

    return (
      <components.ValueContainer {...props}>
        {showDisplay && (
          hasValue ? (
            <DisplayValueWithTooltip text={displayValue} />
          ) : (
            <Placeholder>{props.selectProps.placeholder}</Placeholder>
          )
        )}
        {input}
      </components.ValueContainer>
    );
  }

  return (
    <components.ValueContainer {...props}>
      {hasValue ? (
        <DisplayValueWithTooltip text={displayValue} />
      ) : (
        <Placeholder>{props.selectProps.placeholder}</Placeholder>
      )}
    </components.ValueContainer>
  );
};

export const OutlinedSelect: React.FC<SelectProps> = ({
  options,
  value,
  onChange,
  isMulti = false,
  placeholder = '',
  withSearch = false
}) => {
  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const selectRef = useRef<any>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (selectRef.current && !selectRef.current.contains(event.target)) {
        setMenuIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div ref={selectRef}>
      <StyledSelect
        options={options}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        classNamePrefix="outlined-select"
        isClearable
        isSearchable={withSearch}
        withSearch={withSearch}
        hideSelectedOptions={false}
        components={{
          Option,
          ValueContainer
        }}
        isMulti={isMulti}
        menuIsOpen={menuIsOpen}
        closeMenuOnSelect={!isMulti}
        onMenuOpen={() => setMenuIsOpen(true)}
        onMenuClose={() => setMenuIsOpen(false)}

      />
    </div>
  );
};
